import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  path: string;
  schema?: object;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
}

export const useSEO = ({ title, description, keywords, path, schema, ogImage, ogType = 'website', twitterCard = 'summary_large_image' }: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Canonical URL
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tradfit.co.jp';
    const canonicalUrl = `${siteUrl}${path}`;
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:site_name', 'TradFit株式会社', true);
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Last modified
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, path, schema, ogImage, ogType, twitterCard]);
};

// Schema.org generators
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tradfit.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TradFit株式会社',
    alternateName: 'TradFit Co., Ltd.',
    url: siteUrl,
    logo: 'https://media.tradfit.com/company%2Etradfit%2Ecom/tradfit_logo.png',
    description: '生成AI・音声AIのリーディングカンパニー。最先端のAIソリューション、コンサルティング、実装サービスを提供',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '渋谷区渋谷2-24-12',
      addressLocality: '渋谷',
      addressRegion: '東京都',
      postalCode: '150-0002',
      addressCountry: 'JP'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@tradfit.com',
      contactType: 'customer service',
      availableLanguage: ['Japanese', 'English']
    },
    sameAs: [
      siteUrl
    ]
  };
};

export const generateWebPageSchema = (title: string, description: string, path: string, lang = 'ja-JP') => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tradfit.co.jp';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: `${siteUrl}${path}`,
    publisher: {
      '@type': 'Organization',
      name: 'TradFit株式会社',
      logo: {
        '@type': 'ImageObject',
        url: 'https://media.tradfit.com/company%2E.tradfit%2Ecom/tradfit_logo.png'
      }
    },
    inLanguage: lang,
    isPartOf: {
      '@type': 'WebSite',
      name: 'TradFit株式会社',
      url: siteUrl
    }
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; path: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tradfit.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.path}`
    }))
  };
};

export const generateArticleSchema = (title: string, description: string, date: string, path: string, imageUrl?: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tradfit.com';
  
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    datePublished: date,
    dateModified: date,
    author: {
      '@type': 'Organization',
      name: 'TradFit株式会社'
    },
    publisher: {
      '@type': 'Organization',
      name: 'TradFit株式会社',
      logo: {
        '@type': 'ImageObject',
        url: 'https://media.tradfit.com/company%2Etradfit%2Ecom/tradfit_logo.png'
      }
    },
    url: `${siteUrl}${path}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${path}`
    }
  };

  if (imageUrl) {
    schema.image = {
      '@type': 'ImageObject',
      url: imageUrl
    };
  }

  return schema;
};

export const generateServiceSchema = (name: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tradfit.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: name,
    description: description,
    provider: {
      '@type': 'Organization',
      name: 'TradFit株式会社',
      url: siteUrl
    },
    areaServed: {
      '@type': 'Country',
      name: 'Japan'
    },
    serviceType: 'AI Consulting and Solutions'
  };
};
